import { motion, AnimatePresence } from 'motion/react';
import { Mic, X, Square, Pause, Play } from 'lucide-react';
import { Button } from './ui/button';

interface VoiceRecorderUIProps {
  isRecording: boolean;
  isPaused: boolean; // ⏸️ Added
  duration: number;
  maxDuration: number;
  onCancel: () => void;
  onStop: () => void;
  onStart: () => void;
  onPause?: () => void; // ⏸️ Added
  onResume?: () => void; // ⏸️ Added
}

export function VoiceRecorderUI({ 
  isRecording, 
  isPaused,
  duration, 
  maxDuration, 
  onCancel, 
  onStop,
  onStart,
  onPause,
  onResume
}: VoiceRecorderUIProps) {
  const timeLeft = Math.max(0, (maxDuration - duration) / 1000);
  const progress = (duration / maxDuration) * 100;

  return (
    <div className="relative flex items-center gap-3 w-full">
      <AnimatePresence mode="wait">
        {!isRecording ? (
          <motion.div
            key="idle"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <Button
              variant="ghost"
              size="icon"
              onMouseDown={onStart}
              onTouchStart={onStart}
              className="w-12 h-12 rounded-full bg-brand-gradient text-white shadow-lg active:scale-90 transition-transform"
            >
              <Mic className="w-6 h-6" />
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="recording"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 flex items-center gap-4 bg-orange-500/10 dark:bg-blue-500/10 p-2 rounded-2xl border border-orange-500/30 dark:border-blue-500/30"
          >
            {/* Pulsing Indicator / Timer */}
            <div className="flex items-center gap-2 px-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  isPaused ? onResume?.() : onPause?.()
                }}
                className="w-8 h-8 rounded-full p-0 flex items-center justify-center hover:bg-black/5 dark:hover:bg-white/5 transition-all active:scale-90"
              >
                {!isPaused ? (
                  <div className="relative w-7 h-7 flex items-center justify-center">
                    <motion.div
                      animate={{ scale: [1, 1.4, 1], opacity: [1, 0.4, 1] }}
                      transition={{ repeat: Infinity, duration: 1.5 }}
                      className="w-6 h-6 bg-red-500/20 rounded-full absolute"
                    />
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center relative z-10 shadow-sm">
                      <Pause className="w-2.5 h-2.5 text-white fill-white" />
                    </div>
                  </div>
                ) : (
                  <div className="relative w-7 h-7 flex items-center justify-center">
                    <div className="w-5 h-5 bg-[#999999] rounded-full flex items-center justify-center relative z-10 shadow-sm">
                      <Play className="w-2.5 h-2.5 text-white fill-white ml-0.5" />
                    </div>
                  </div>
                )}
              </Button>
              <span className={`text-sm font-bold tabular-nums shrink-0 ${isPaused ? 'text-[#999999]' : 'text-red-500'}`}>
                {timeLeft.toFixed(1)}s
              </span>
            </div>

            {/* Progress Bar */}
            <div className="flex-1 h-1.5 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
              <motion.div 
                className={`h-full transition-all duration-300 ${isPaused ? 'bg-[#999999]' : 'bg-linear-to-r from-orange-500 to-red-500 dark:from-blue-500 dark:to-cyan-500'}`}
                style={{ width: `${progress}%` }}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 pr-2">

              {/* Cancel Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onCancel}
                className="w-8 h-8 rounded-full text-[#666666] hover:bg-black/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
